import { createContext, useContext, useEffect, useState } from 'react';

interface User {
  uid: string;
  email: string | null;
  name?: string;
}

interface AuthContextType {
  session: User | null;
  loading: boolean;
  signIn: (email: string) => Promise<{ error: any }>;
  verifyOtp: (email: string, token: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Check for local session on mount (Offline-First DB)
    const loadSession = () => {
      try {
        const savedUser = localStorage.getItem('habl_local_user');
        if (savedUser) {
          setSession(JSON.parse(savedUser));
        }
      } catch (e) {
        console.error('Failed to load local session', e);
      } finally {
        setLoading(false);
      }
    };
    
    loadSession();
  }, []);

  const signIn = async (email: string) => {
    try {
      // Offline-First Instant Login: No emails sent, no API keys needed.
      // This removes all friction and database costs for V1.
      const user: User = {
        uid: crypto.randomUUID(), // Generate a unique ID for the device
        email: email,
      };
      
      localStorage.setItem('habl_local_user', JSON.stringify(user));
      setSession(user);
      
      return { error: null };
    } catch (err: any) {
      console.error('Sign in error:', err);
      return { error: err.message || 'Failed to create local session' };
    }
  };

  const verifyOtp = async (_email: string, _token: string) => {
    return { error: null };
  };

  const signOut = async () => {
    // Clear Local Storage
    localStorage.removeItem('habl_local_user');
    
    // Reset State
    setSession(null);
    
    // Force Reload to clear memory
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ session, loading, signIn, verifyOtp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
